import { useCallback, useEffect, useState, type ReactNode, type FormEvent } from "react";
import { api, errorMessage } from "../../lib/api";
import { formatMYR } from "../../lib/format";
import { Alert, Button } from "../../components/Form";

/* Owner-only screens: kept in BM (not part of the UI translations). */

type KV = { key: string; n: number; visitors?: number };
type Day = { day: string; visitors: number; views: number; downloads: number; newUsers: number; paid: number };
export type AdminStatsData = {
  today: string;
  since: string;
  todayStats: { visitors: number; views: number; downloads: number; downloadsFailed: number; newUsers: number; ordersStarted: number; ordersPaid: number; revenueSen: number; papersReady: number; papersFailed: number };
  totals: { visitors: number; views: number; downloads: number; users: number; usersLicensed: number; usersNoLicence: number; notSubmitted: number; awaitingVerify: number; expired: number; invitesUnredeemed: number; paidOrders: number; revenueSen: number; papersReady: number; openReports: number };
  daily: Day[];
  hours: { hour: number; views: number; visitors?: number; downloads?: number }[];
  pages: KV[];
  langs: KV[];
  devices: KV[];
  referrers: KV[];
  downloadsBy: KV[];
  notSubmitted: { reference_code: string; buyer_email: string; kind: string; amount_due_sen: number; created_at: string; hold_expires_at: string | null }[];
  invitesUnredeemed: { email: string; created_at: string; expires_at: string; reference_code: string }[];
  recentUsers: { email: string; created_at: string; licensed: boolean }[];
};

const dt = (s: string | null) => (s ? new Date(s).toLocaleString("ms-MY", { timeZone: "Asia/Kuala_Lumpur", dateStyle: "medium", timeStyle: "short" }) : "—");
const shortDay = (d: string) => `${Number(d.slice(8, 10))}/${Number(d.slice(5, 7))}`;
const dayLabel = (d: string) => new Date(`${d}T00:00:00+08:00`).toLocaleDateString("ms-MY", { timeZone: "Asia/Kuala_Lumpur", day: "numeric", month: "short" });
const LANG: Record<string, string> = { ms: "BM", en: "English", zh: "中文", ta: "தமிழ்" };
const DEVICE: Record<string, string> = { mobile: "📱 Telefon", tablet: "📲 Tablet", desktop: "💻 Komputer" };
const PAGE: Record<string, string> = {
  "/": "Laman utama", "/beli": "Beli", "/uasa": "Penjana kertas", "/belajar": "JomBelajar", "/login": "Log masuk", "/affiliate": "Affiliate",
  "/kredit": "Kredit", "/belajar/permainan": "Hab permainan", "/belajar/sifir": "Sifir Roket", "/redeem": "Aktifkan lesen",
};

const PERIODS = [{ days: 1, label: "Hari ini" }, { days: 7, label: "7 hari" }, { days: 14, label: "14 hari" }, { days: 30, label: "30 hari" }];

export function StatsPanel() {
  const [days, setDays] = useState(() => { try { return Number(localStorage.getItem("admin.statsDays")) || 1; } catch { return 1; } });
  const pickDays = (d: number) => { setDays(d); try { localStorage.setItem("admin.statsDays", String(d)); } catch { /* ignore */ } };
  const periodName = PERIODS.find((p) => p.days === days)?.label ?? `${days} hari`;
  const [s, setS] = useState<AdminStatsData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [at, setAt] = useState<Date | null>(null);
  const load = useCallback(() => {
    api<{ stats: AdminStatsData }>(`/api/admin/stats?days=${days}`)
      .then((r) => { setS(r.stats); setAt(new Date()); setError(null); })
      .catch((e) => setError(errorMessage(e)));
  }, [days]);
  useEffect(() => { load(); const t = setInterval(load, 60_000); return () => clearInterval(t); }, [load]);

  if (error) return <Alert kind="error">{error}</Alert>;
  if (!s) return <p className="text-sm text-muted">Memuatkan statistik…</p>;
  const td = s.todayStats, tt = s.totals;

  return (
    <div className="space-y-8">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h2 className="text-lg font-semibold">Hari ini · {dayLabel(s.today)}</h2>
          <p className="text-xs text-muted">Masa Malaysia. Paparan anda sendiri (admin) tidak dikira. Dikemas kini {at?.toLocaleTimeString("ms-MY", { timeZone: "Asia/Kuala_Lumpur", timeStyle: "short" })} · auto setiap minit.</p>
        </div>
        <Button variant="secondary" onClick={load}>↻ Muat semula</Button>
      </div>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
        <Tile label="Pelawat" value={td.visitors} sub={`${td.views} paparan halaman`} />
        <Tile label="Muat turun" value={td.downloads} sub={td.downloadsFailed ? `${td.downloadsFailed} gagal` : "tiada yang gagal"} warn={td.downloadsFailed > 0} />
        <Tile label="Ahli baharu" value={td.newUsers} sub={`${tt.users} ahli semua`} />
        <Tile label="Pesanan dibayar" value={td.ordersPaid} sub={`${formatMYR(td.revenueSen)} hari ini`} />
        <Tile label="Kertas dijana" value={td.papersReady} sub={td.papersFailed ? `${td.papersFailed} gagal` : "tiada yang gagal"} warn={td.papersFailed > 0} />
      </div>

      <section>
        <h2 className="mb-2 text-lg font-semibold">Ahli & pesanan</h2>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
          <Tile small label="Ahli berdaftar" value={tt.users} />
          <Tile small label="Ada lesen" value={tt.usersLicensed} />
          <Tile small label="Daftar, tiada lesen" value={tt.usersNoLicence} />
          <Tile small label="Belum submit bayaran" value={tt.notSubmitted} warn={tt.notSubmitted > 0} />
          <Tile small label="Tunggu pengesahan" value={tt.awaitingVerify} warn={tt.awaitingVerify > 0} />
          <Tile small label="Belum aktifkan lesen" value={tt.invitesUnredeemed} />
        </div>
        <p className="mt-2 text-xs text-muted">Jumlah jualan: {tt.paidOrders} pesanan · {formatMYR(tt.revenueSen)} · {tt.expired} pesanan tamat tempoh tanpa bayar.</p>
      </section>

      <section>
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
          <h2 className="text-lg font-semibold">Pelawat · {periodName}</h2>
          <div className="flex flex-wrap gap-1" role="group" aria-label="Tempoh">
            {PERIODS.map((p) => (
              <button key={p.days} type="button" aria-pressed={days === p.days} onClick={() => pickDays(p.days)}
                className={`rounded-full px-3 py-1.5 text-xs font-semibold ${days === p.days ? "bg-brand text-white" : "border border-surface-line"}`}>{p.label}</button>
            ))}
          </div>
        </div>
        <div className="mb-3 grid grid-cols-3 gap-3">
          <Tile small label="Pelawat" value={tt.visitors} />
          <Tile small label="Paparan halaman" value={tt.views} />
          <Tile small label="Muat turun" value={tt.downloads} />
        </div>
        {days === 1
          ? <Bars empty="Belum ada pelawat hari ini." data={s.hours.map((h) => ({ label: h.hour % 6 === 0 ? `${String(h.hour).padStart(2, "0")}:00` : "", value: h.visitors ?? h.views, tip: `${String(h.hour).padStart(2, "0")}:00–${String(h.hour).padStart(2, "0")}:59 · ${h.visitors ?? "?"} pelawat · ${h.views} paparan · ${h.downloads ?? 0} muat turun` }))} />
          : <Bars empty="Belum ada pelawat dalam tempoh ini." data={s.daily.map((d, i, all) => ({ label: (all.length - 1 - i) % Math.ceil(all.length / 7) === 0 ? shortDay(d.day) : "", value: d.visitors, tip: `${dayLabel(d.day)}: ${d.visitors} pelawat · ${d.views} paparan · ${d.downloads} muat turun · ${d.newUsers} ahli baharu · ${d.paid} bayaran` }))} />}
        <p className="mt-1 text-xs text-muted">{days === 1 ? "Pelawat ikut jam, hari ini." : "Pelawat sehari."} Tekan bar untuk butiran. Pecahan di bawah ikut tempoh yang dipilih.</p>
      </section>

      <VisitorCalendar />

      <div className="grid gap-6 md:grid-cols-2">
        <Breakdown title={`Halaman paling dilawati · ${periodName}`} rows={s.pages.map((p) => ({ label: PAGE[p.key] ? `${PAGE[p.key]} (${p.key})` : p.key, n: p.n, note: `${p.visitors ?? 0} pelawat` }))} unit="paparan" />
        <Breakdown title={`Dari mana pelawat datang · ${periodName}`} rows={s.referrers.map((r) => ({ label: r.key === "terus" ? "Terus / WhatsApp / tiada rujukan" : r.key, n: r.n }))} unit="pelawat" />
        <Breakdown title={`Peranti · ${periodName}`} rows={s.devices.map((r) => ({ label: DEVICE[r.key] ?? r.key, n: r.n }))} unit="pelawat" />
        <Breakdown title={`Bahasa UI · ${periodName}`} rows={s.langs.map((r) => ({ label: LANG[r.key] ?? r.key, n: r.n }))} unit="pelawat" />
        <Breakdown title={`Muat turun ikut subjek · ${periodName}`} rows={s.downloadsBy.map((r) => ({ label: r.key || "lain-lain", n: r.n }))} unit="fail" />
      </div>

      <section>
        <h2 className="text-lg font-semibold">Belum submit bayaran ({tt.notSubmitted})</h2>
        <p className="mb-2 text-sm text-muted">Dah isi borang beli tapi belum hantar bukti bayaran. Boleh follow up melalui emel.</p>
        <Table empty="Tiada." head={["Rujukan", "Emel", "Jenis", "Jumlah", "Dicipta", "Tamat"]}
          rows={s.notSubmitted.map((o) => [<span className="font-mono">{o.reference_code}</span>, <a className="underline" href={`mailto:${o.buyer_email}?subject=${encodeURIComponent(`Pesanan JomCerdik ${o.reference_code}`)}`}>{o.buyer_email}</a>, o.kind === "topup" ? "Tambah kertas" : "Lesen", formatMYR(o.amount_due_sen), dt(o.created_at), dt(o.hold_expires_at)])} />
      </section>

      <section>
        <h2 className="text-lg font-semibold">Dah bayar, belum aktifkan lesen ({tt.invitesUnredeemed})</h2>
        <p className="mb-2 text-sm text-muted">Jemputan dihantar tapi belum ditekan. Hantar semula dari tab Pesanan jika perlu.</p>
        <Table empty="Tiada." head={["Rujukan", "Emel", "Dihantar", "Tamat"]}
          rows={s.invitesUnredeemed.map((i) => [<span className="font-mono">{i.reference_code}</span>, i.email, dt(i.created_at), dt(i.expires_at)])} />
      </section>

      <section>
        <h2 className="mb-2 text-lg font-semibold">Ahli terkini</h2>
        <Table empty="Belum ada ahli." head={["Emel", "Daftar", "Lesen"]}
          rows={s.recentUsers.map((u) => [u.email, dt(u.created_at), u.licensed ? "✅ Ada" : "— Tiada"])} />
      </section>
    </div>
  );
}

function VisitorCalendar() {
  const today = new Intl.DateTimeFormat("en-CA", { timeZone: "Asia/Kuala_Lumpur", year: "numeric", month: "2-digit", day: "2-digit" }).format(new Date());
  const [from, setFrom] = useState(today);
  const [to, setTo] = useState(today);
  const [data, setData] = useState<{ visitors: number; views: number; downloads: number; daily: { day: string; visitors: number; views: number; downloads: number }[] } | null>(null);
  const [rangeError, setRangeError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  async function search(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!from || !to || from > to) { setRangeError("Pilih tarikh mula dan akhir yang sah."); return; }
    if (Date.parse(to + "T00:00:00Z") - Date.parse(from + "T00:00:00Z") > 365 * 86400000) { setRangeError("Pilih tempoh maksimum 366 hari."); return; }
    setBusy(true); setRangeError(null); setData(null);
    try {
      const r = await api<{ range: NonNullable<typeof data> }>(`/api/admin/stats/range?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`);
      setData(r.range);
    } catch (err) { setRangeError(errorMessage(err)); } finally { setBusy(false); }
  }
  return (
    <section className="space-y-3">
      <h2 className="text-lg font-semibold">📅 Semak pelawat mengikut tarikh</h2>
      <p className="text-sm text-muted">Pilih satu hari atau tempoh tarikh. Masa Malaysia; paparan yang dikenal pasti sebagai admin tidak dikira.</p>
      <form onSubmit={search} className="flex flex-wrap items-end gap-3">
        <label className="text-sm font-medium">Dari tarikh<input type="date" required value={from} onChange={(e) => setFrom(e.target.value)} className="mt-1 block rounded-md border border-surface-line bg-card px-3 py-2" /></label>
        <label className="text-sm font-medium">Hingga tarikh<input type="date" required value={to} onChange={(e) => setTo(e.target.value)} className="mt-1 block rounded-md border border-surface-line bg-card px-3 py-2" /></label>
        <Button type="submit" disabled={busy}>{busy ? "Memuatkan…" : "Lihat statistik"}</Button>
      </form>
      {rangeError && <Alert kind="error">{rangeError}</Alert>}
      {data && <>
        <div className="grid grid-cols-3 gap-3">
          <Tile small label="Pelawat unik" value={data.visitors} />
          <Tile small label="Paparan halaman" value={data.views} />
          <Tile small label="Muat turun" value={data.downloads} />
        </div>
        <p className="text-xs text-muted">Pelawat unik dikira sekali untuk seluruh tempoh; jumlah pelawat harian boleh berulang. Lawatan tanpa log masuk tidak dapat dipastikan sebagai admin.</p>
        <div className="max-h-80 overflow-auto rounded-card border border-surface-line">
          <table className="w-full text-left text-sm"><thead className="sticky top-0 bg-surface-muted"><tr><th className="p-2">Tarikh</th><th className="p-2">Pelawat</th><th className="p-2">Paparan</th><th className="p-2">Muat turun</th></tr></thead>
            <tbody>{data.daily.map((d) => <tr key={d.day} className="border-t border-surface-line"><td className="p-2">{dayLabel(d.day)} {d.day.slice(0, 4)}</td><td className="p-2">{d.visitors}</td><td className="p-2">{d.views}</td><td className="p-2">{d.downloads}</td></tr>)}</tbody>
          </table>
        </div>
      </>}
    </section>
  );
}

function Tile({ label, value, sub, warn, small }: { label: string; value: number; sub?: string; warn?: boolean; small?: boolean }) {
  return (
    <div className={`rounded-card border p-3 ${warn ? "border-amber-300 bg-amber-50 dark:bg-amber-950/30" : "border-surface-line bg-card"}`}>
      <p className="text-xs font-medium text-muted">{label}</p>
      <p className={`${small ? "text-2xl" : "text-3xl"} font-bold tabular-nums`}>{value.toLocaleString("ms-MY")}</p>
      {sub && <p className={`text-xs ${warn ? "font-semibold text-amber-700 dark:text-amber-400" : "text-muted"}`}>{sub}</p>}
    </div>
  );
}

/** Single-series column chart: one hue, rounded tops on a shared baseline, a tooltip per column. */
function Bars({ data, small, empty }: { data: { label: string; value: number; tip: string }[]; small?: boolean; empty: string }) {
  const [hover, setHover] = useState<number | null>(null);
  const max = Math.max(1, ...data.map((d) => d.value));
  const h = small ? 96 : 160;
  return (
    <div className="relative rounded-card border border-surface-line bg-card p-3">
      <div className="flex items-end gap-[2px]" style={{ height: h }} onMouseLeave={() => setHover(null)}>
        {data.map((d, i) => (
          <button key={i} type="button" aria-label={d.tip} onMouseEnter={() => setHover(i)} onFocus={() => setHover(i)} onClick={() => setHover(i)}
            className="group flex h-full flex-1 flex-col justify-end">
            {!small && d.value > 0 && <span className="mb-0.5 text-center text-[10px] tabular-nums text-muted">{d.value}</span>}
            <span className={`block w-full rounded-t-[4px] ${hover === i ? "bg-brand-dark dark:bg-teal-300" : "bg-teal-500 dark:bg-teal-400"}`}
              style={{ height: `${d.value ? Math.max(2, (d.value / max) * (h - 16)) : 0}px` }} />
          </button>
        ))}
      </div>
      <div className="mt-1 flex gap-[2px] border-t border-surface-line pt-1">
        {data.map((d, i) => <span key={i} className="relative h-3 flex-1"><span className="absolute left-1/2 -translate-x-1/2 whitespace-nowrap text-[10px] text-muted">{d.label}</span></span>)}
      </div>
      {data.every((d) => d.value === 0) && <p className="pointer-events-none absolute inset-0 grid place-items-center text-sm text-muted">{empty}</p>}
      {hover !== null && data[hover] && (
        <p className="pointer-events-none absolute right-3 top-2 max-w-[80%] rounded-md bg-slate-900 px-2 py-1 text-xs text-white shadow">{data[hover]!.tip}</p>
      )}
    </div>
  );
}

function Breakdown({ title, rows, unit }: { title: string; rows: { label: string; n: number; note?: string }[]; unit: string }) {
  const max = Math.max(1, ...rows.map((r) => r.n));
  return (
    <section>
      <h3 className="mb-2 font-semibold">{title}</h3>
      {rows.length === 0 ? <p className="text-sm text-muted">Belum ada data.</p> : (
        <ul className="space-y-1.5 text-sm">
          {rows.map((r) => (
            <li key={r.label}>
              <div className="flex justify-between gap-2"><span className="truncate">{r.label}</span><span className="shrink-0 tabular-nums text-muted">{r.n} {unit}{r.note ? ` · ${r.note}` : ""}</span></div>
              <div className="h-1.5 rounded-full bg-surface-muted"><div className="h-full rounded-full bg-teal-500" style={{ width: `${(r.n / max) * 100}%` }} /></div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function Table({ head, rows, empty }: { head: string[]; rows: ReactNode[][]; empty: string }) {
  if (rows.length === 0) return <p className="text-sm text-muted">{empty}</p>;
  return (
    <div className="overflow-x-auto rounded-card border border-surface-line">
      <table className="w-full text-left text-sm">
        <thead className="bg-surface-muted text-xs text-muted"><tr>{head.map((h) => <th key={h} className="px-3 py-2 font-semibold">{h}</th>)}</tr></thead>
        <tbody className="divide-y divide-surface-line">
          {rows.map((r, i) => <tr key={i}>{r.map((c, j) => <td key={j} className="whitespace-nowrap px-3 py-2">{c}</td>)}</tr>)}
        </tbody>
      </table>
    </div>
  );
}

/* ── Problem reports ─────────────────────────────────────────────────────── */
type Report = { id: string; created_at: string; kind: string; email: string | null; path: string | null; message: string; details: Record<string, unknown>; user_agent: string | null; status: string; resolved_at: string | null };
const KIND: Record<string, { label: string; icon: string }> = {
  bug: { label: "Laporan pengguna", icon: "🐞" },
  download_failed: { label: "Muat turun gagal", icon: "📄" },
  client_error: { label: "Ralat pelayar", icon: "⚠️" },
  server_error: { label: "Ralat server", icon: "🛑" },
};

export function ReportsPanel({ onCount }: { onCount?: (n: number) => void }) {
  const [status, setStatus] = useState<"open" | "resolved" | "all">("open");
  const [kind, setKind] = useState("all");
  const [rows, setRows] = useState<Report[] | null>(null);
  const [byKind, setByKind] = useState<Record<string, number>>({});
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const load = useCallback(() => {
    api<{ reports: Report[]; openByKind: Record<string, number> }>(`/api/admin/reports?status=${status}&kind=${kind}`)
      .then((r) => { setRows(r.reports); setByKind(r.openByKind); onCount?.(Object.values(r.openByKind).reduce((a, b) => a + b, 0)); })
      .catch((e) => setError(errorMessage(e)));
  }, [status, kind, onCount]);
  useEffect(load, [load]);

  async function act(id: string, action: "resolve" | "reopen") {
    setBusy(id);
    try { await api(`/api/admin/reports/${id}/${action}`, { method: "POST", json: {} }); load(); }
    catch (e) { setError(errorMessage(e)); } finally { setBusy(null); }
  }

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        {Object.entries(KIND).map(([k, v]) => (
          <button key={k} type="button" onClick={() => setKind(kind === k ? "all" : k)} aria-pressed={kind === k}
            className={`rounded-card border p-3 text-left ${kind === k ? "border-brand ring-2 ring-brand/30" : "border-surface-line"} ${byKind[k] ? "bg-amber-50 dark:bg-amber-950/30" : "bg-card"}`}>
            <p className="text-xs text-muted">{v.icon} {v.label}</p>
            <p className="text-2xl font-bold tabular-nums">{byKind[k] ?? 0}</p>
            <p className="text-xs text-muted">belum selesai</p>
          </button>
        ))}
      </div>
      <div className="flex flex-wrap gap-1">
        {(["open", "resolved", "all"] as const).map((st) => (
          <button key={st} type="button" aria-pressed={status === st} onClick={() => setStatus(st)}
            className={`rounded-full px-3 py-1 text-xs font-semibold ${status === st ? "bg-brand text-white" : "border border-surface-line"}`}>
            {st === "open" ? "Belum selesai" : st === "resolved" ? "Selesai" : "Semua"}
          </button>
        ))}
        <Button variant="secondary" onClick={load} className="ml-auto">↻ Muat semula</Button>
      </div>
      {error && <Alert kind="error">{error}</Alert>}
      {rows?.length === 0 && <Alert kind="success">Tiada laporan {status === "open" ? "yang belum selesai" : ""}. 👍</Alert>}
      <ul className="space-y-3">
        {rows?.map((r) => {
          const k = KIND[r.kind] ?? { label: r.kind, icon: "•" };
          const repeat = Number(r.details?.repeat ?? 1);
          const extra = Object.entries(r.details ?? {}).filter(([key]) => !["repeat", "last_at", "stack"].includes(key) && r.details[key] !== null && r.details[key] !== "");
          return (
            <li key={r.id} className={`rounded-card border p-3 text-sm ${r.status === "open" ? "border-surface-line bg-card" : "border-surface-line opacity-70"}`}>
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <p className="text-xs text-muted">{k.icon} {k.label} · {dt(r.created_at)}{repeat > 1 ? ` · berulang ${repeat}×` : ""} · {r.path ?? "—"}</p>
                  <p className="mt-1 whitespace-pre-wrap break-words font-medium">{r.message}</p>
                  <p className="mt-1 text-xs text-muted">{r.email ? <a className="underline" href={`mailto:${r.email}?subject=${encodeURIComponent("Laporan masalah JomCerdik")}`}>{r.email}</a> : "Pelawat (tidak log masuk)"}</p>
                  {extra.length > 0 && <p className="mt-1 break-words font-mono text-[11px] text-muted">{extra.map(([a, b]) => `${a}=${String(b)}`).join(" · ")}</p>}
                  {(typeof r.details?.stack === "string" || r.user_agent) && (
                    <details className="mt-1 text-[11px] text-muted"><summary className="cursor-pointer">Butiran teknikal</summary>
                      {typeof r.details?.stack === "string" && <pre className="mt-1 max-h-40 overflow-auto whitespace-pre-wrap">{r.details.stack}</pre>}
                      {r.user_agent && <p className="mt-1 break-words">{r.user_agent}</p>}
                    </details>
                  )}
                </div>
                {r.status === "open"
                  ? <Button onClick={() => act(r.id, "resolve")} busy={busy === r.id}>✓ Selesai</Button>
                  : <Button variant="secondary" onClick={() => act(r.id, "reopen")} busy={busy === r.id}>Buka semula</Button>}
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
